import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionWizard, ChildSubscriptionConfig } from './SubscriptionWizardContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Building, 
  Users,
  Package,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// Removed Dialog imports as they are no longer needed

// Removed CustomerData interface as it's no longer needed

const Step5bSplitConfig = () => {
  const navigate = useNavigate();
  const { state, setChildSubscriptions } = useSubscriptionWizard();
  // Removed unused state variables as child subscriptions are now auto-generated from Step2

  useEffect(() => {
    if (!state.selectedCustomer || state.subscriptionType !== 'split' || state.selectedPlans.length === 0) {
      navigate('../step1', { relative: 'path' });
      return;
    }
    
    // Use the child customers already selected in Step2 instead of fetching all customers
    if (state.selectedChildCustomers.length > 0) {
      // Convert selectedChildCustomers to the format expected by this component
      const childSubscriptions = state.selectedChildCustomers.map((customer, index) => {
        // Initialize plan allocations with 0 for all plans
        const planAllocations: { [planId: string]: number } = {};
        state.selectedPlans.forEach(plan => {
          planAllocations[plan.subscription_plan_id] = 0;
        });

        return {
          id: `child_${index}`,
          name: `${customer.name} - Assinatura`, // Auto-generate name based on customer
          customer: {
            id: customer.id,
            name: customer.name,
            legal_name: customer.legal_name || customer.name
          },
          participants: [], // No participants for now, can be added later if needed
          planAllocations
        } as ChildSubscriptionConfig;
      });

      setChildSubscriptions(childSubscriptions);
    }
  }, [state.selectedCustomer, state.subscriptionType, state.selectedPlans, state.selectedChildCustomers, navigate]);

  // Removed addChildSubscription function as child subscriptions are now created automatically from Step2

  // Removed removeChildSubscription function as child subscriptions are now auto-generated from Step2

  const updatePlanAllocation = (childIndex: number, planId: string, quantity: number) => {
    const updatedChildSubs = [...state.childSubscriptions];
    updatedChildSubs[childIndex].planAllocations[planId] = Math.max(0, quantity);
    setChildSubscriptions(updatedChildSubs);
  };

  const getTotalAllocated = (planId: string) => {
    return state.childSubscriptions.reduce((total, child) => {
      return total + (child.planAllocations[planId] || 0);
    }, 0);
  };

  const getRemaining = (planId: string) => {
    const plan = state.selectedPlans.find(p => p.subscription_plan_id === planId);
    if (!plan) return 0;
    return plan.quantity - getTotalAllocated(planId);
  };

  const isValidConfiguration = () => {
    if (state.childSubscriptions.length === 0) return false;
    
    return state.selectedPlans.every(plan => {
      const allocated = getTotalAllocated(plan.subscription_plan_id);
      return allocated === plan.quantity;
    });
  };

  const handleNext = () => {
    if (!isValidConfiguration()) {
      toast.error('Por favor, aloque todas as quantidades dos planos antes de continuar');
      return;
    }
    navigate('../step6', { relative: 'path' });
  };

  return (
    <SubscriptionWizardLayout
      currentStep={6}
      totalSteps={7}
      title="Configuração de Split"
      description="Configure como os planos serão divididos entre as assinaturas filho"
      onNext={handleNext}
      onPrevious={() => navigate('../step5', { relative: 'path' })}
      isNextDisabled={!isValidConfiguration()}
    >
      <div className="space-y-6">
        {/* Summary */}
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-extrabold text-blue-800">Assinatura Principal</div>
              <div className="font-extrabold mt-1 text-blue-900">{state.selectedCustomer?.name}</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-extrabold text-blue-800">Total de Planos</div>
              <div className="font-extrabold mt-1 text-blue-900">{state.selectedPlans.length} planos</div>
            </div>
          </div>
        </div>

        {/* Child Subscriptions - Auto-generated from Step2 */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-extrabold">Assinaturas Filho</h3>
            <div className="text-sm text-muted-foreground">
              {state.childSubscriptions.length} assinatura(s) filho baseada(s) nos clientes selecionados no Step 2
            </div>
          </div>

          {state.childSubscriptions.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">Nenhuma assinatura filho configurada</p>
              <p className="text-sm text-gray-400">Volte ao Step 2 para selecionar os clientes filho</p>
            </div>
          ) : (
            <div className="space-y-4">
              {state.childSubscriptions.map((childSub, index) => (
                <Card key={childSub.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{childSub.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{childSub.customer.name}</p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Plan Allocation Table */}
        {state.childSubscriptions.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-extrabold">Alocação de Planos</h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plano</TableHead>
                    <TableHead className="text-center">Quantidade Total</TableHead>
                    {state.childSubscriptions.map(childSub => (
                      <TableHead key={childSub.id} className="text-center">
                        {childSub.name}
                      </TableHead>
                    ))}
                    <TableHead className="text-center">Restante</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.selectedPlans.map(plan => {
                    const remaining = getRemaining(plan.subscription_plan_id);
                    const isFullyAllocated = remaining === 0;
                    
                    return (
                      <TableRow key={plan.subscription_plan_id}>
                        <TableCell>
                          <div>
                            <div className="font-extrabold">{plan.subscription_plan.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {plan.subscription_plan.sku}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{plan.quantity}</Badge>
                        </TableCell>
                        {state.childSubscriptions.map((childSub, childIndex) => (
                          <TableCell key={childSub.id} className="text-center">
                            <Input
                              type="number"
                              min="0"
                              max={plan.quantity}
                              value={childSub.planAllocations[plan.subscription_plan_id] || 0}
                              onChange={(e) => updatePlanAllocation(
                                childIndex, 
                                plan.subscription_plan_id, 
                                parseInt(e.target.value) || 0
                              )}
                              className="w-20 text-center"
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            {isFullyAllocated ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm">{remaining}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Validation Summary */}
        {state.childSubscriptions.length > 0 && (
          <div className={`p-4 rounded-md ${isValidConfiguration() ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-center space-x-2">
              {isValidConfiguration() ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-extrabold text-green-800">Configuração válida</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <span className="font-extrabold text-yellow-800">Configuração incompleta</span>
                </>
              )}
            </div>
            <p className="text-sm mt-1 text-muted-foreground">
              {isValidConfiguration() 
                ? 'Todas as quantidades foram alocadas corretamente'
                : 'Aloque todas as quantidades dos planos para continuar'
              }
            </p>
          </div>
        )}
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step5bSplitConfig;